/**
 * ErrorBoundary — global React error boundary.
 *
 * Catches uncaught render errors anywhere in the tree and displays a friendly
 * recovery screen instead of a blank black page. Uses @kantera/ui theme tokens
 * with inline CSSProperties (no Tailwind).
 *
 * One error class is not an error: a lazy() route chunk that no longer exists
 * because Kantera was deployed while this tab was open. That case reloads once
 * (guarded — see utils/chunkReload.ts) and says so, rather than telling a
 * signed-in coach "the team has been notified" about a routine release. If the
 * guard refuses (the chunk is genuinely gone), the ordinary screen is shown and
 * the error IS reported.
 *
 * This component sits OUTSIDE the I18nProvider, so it reads the i18next
 * instance directly instead of through the hook.
 */

import React from 'react';
import * as Sentry from '@sentry/react';
import { i18n, theme } from '@kantera/ui';
import { isChunkLoadError, reloadOnceForChunk } from '../utils/chunkReload';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

type ErrorKind =
  /** An ordinary render error. */
  | 'generic'
  /** A stale-deploy chunk failure; reload not yet attempted or in flight. */
  | 'stale-chunk'
  /** A chunk failure the reload guard refused — treated as a real error. */
  | 'stale-chunk-refused';

interface ErrorBoundaryState {
  hasError: boolean;
  message: string;
  kind: ErrorKind;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, message: '', kind: 'generic' };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    const message = error instanceof Error ? error.message : i18n.t('errorBoundary.body');
    return {
      hasError: true,
      message,
      kind: isChunkLoadError(error) ? 'stale-chunk' : 'generic',
    };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    if (isChunkLoadError(error)) {
      if (reloadOnceForChunk()) {
        // The page is on its way to a fresh index.html. Nothing to report.
        return;
      }
      // Reloaded within the window and it still failed: the chunk is really
      // missing. Fall through to the ordinary screen and DO report it.
      this.setState({ kind: 'stale-chunk-refused' });
    }

    Sentry.captureException(error, {
      extra: {
        componentStack: info.componentStack,
      },
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, message: '', kind: 'generic' });
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const updating = this.state.kind === 'stale-chunk';

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: theme.colors.bg.primary,
          padding: theme.spacing.xl,
        }}
        role={updating ? 'status' : 'alert'}
        aria-live={updating ? 'polite' : 'assertive'}
      >
        <div
          style={{
            maxWidth: '480px',
            width: '100%',
            backgroundColor: theme.colors.bg.secondary,
            border: `1px solid ${theme.colors.border.primary}`,
            borderRadius: theme.radius.lg,
            padding: theme.spacing['2xl'],
            textAlign: 'center',
          }}
        >
          {/* Icon */}
          <div
            aria-hidden="true"
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: updating
                ? theme.colors.accent.rgbA(0.12)
                : theme.colors.danger.rgbA(0.09),
              border: `1px solid ${
                updating ? theme.colors.accent.rgbA(0.3) : theme.colors.danger.rgbA(0.27)
              }`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '24px',
              color: updating ? theme.colors.accent.primary : theme.colors.danger.primary,
            }}
          >
            {updating ? '↻' : '!'}
          </div>

          <h1
            style={{
              fontSize: theme.typography.fontSize.xl.size,
              fontWeight: 700,
              color: theme.colors.text.primary,
              margin: `0 0 ${theme.spacing.sm}`,
            }}
          >
            {updating ? i18n.t('errorBoundary.updatedTitle') : i18n.t('errorBoundary.title')}
          </h1>

          <p
            style={{
              fontSize: theme.typography.fontSize.sm.size,
              color: theme.colors.text.secondary,
              lineHeight: 1.6,
              margin: updating ? 0 : `0 0 ${theme.spacing.lg}`,
            }}
          >
            {updating ? (
              i18n.t('errorBoundary.updatedBody')
            ) : (
              <>
                {i18n.t('errorBoundary.body')}{' '}
                {import.meta.env['VITE_SENTRY_DSN'] ? `${i18n.t('errorBoundary.notified')} ` : ''}
                {i18n.t('errorBoundary.returnHint')}
              </>
            )}
          </p>

          {!updating && this.state.message && (
            <p
              style={{
                fontSize: '12px',
                color: theme.colors.text.muted,
                fontFamily: 'monospace',
                backgroundColor: theme.colors.bg.primary,
                borderRadius: theme.radius.sm,
                padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                marginBottom: theme.spacing.lg,
                wordBreak: 'break-word',
                textAlign: 'left',
              }}
            >
              {this.state.message}
            </p>
          )}

          {!updating && (
            <button
              onClick={this.handleReset}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: `${theme.spacing.sm} ${theme.spacing.xl}`,
                backgroundColor: theme.colors.accent.primary,
                color: '#fff',
                border: 'none',
                borderRadius: theme.radius.md,
                fontSize: theme.typography.fontSize.sm.size,
                fontWeight: 600,
                cursor: 'pointer',
                transition: theme.transitions.fast,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                  theme.colors.accent.hover;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                  theme.colors.accent.primary;
              }}
            >
              {i18n.t('errorBoundary.goToDashboard')}
            </button>
          )}
        </div>
      </div>
    );
  }
}
